import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect, useRef } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/report";
import { formatNumber } from "../../../utils/formatNumber";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";

export default function DailyPurchaseSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];
  const printRef = useRef();

  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  const [activityRows, setActivityRows] = useState([]);
  const [paymentRows, setPaymentRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `รายงานสรุปยอดซื้อ_${startDate}_to_${endDate}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getDailyPurchaseSummary(startDate, endDate);
        const data = res.data?.data || {};
        setActivityRows(data.activities || []);
        setPaymentRows(data.payments || []);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  const sumActivity = activityRows.reduce(
    (acc, row) => ({
      count: acc.count + (row.count || 0),
      amount: acc.amount + (row.amount || 0),
      vat: acc.vat + (row.vat || 0),
      total: acc.total + (row.total || 0),
    }),
    { count: 0, amount: 0, vat: 0, total: 0 }
  );

  const totalPayment = paymentRows.reduce(
    (sum, row) => sum + (row.total || 0),
    0
  );

  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportActivities = () => {
    const dataset = [
      ...activityRows.map((r) => ({
        กิจกรรมการเปิดบิล: r.name ?? "",
        จำนวนตั๋ว: Number(r.count ?? 0),
        ยอดก่อนรวมภาษี: Number(r.amount ?? 0).toFixed(2),
        ยอดภาษี: Number(r.vat ?? 0).toFixed(2),
        ยอดสุทธิ: Number(r.total ?? 0).toFixed(2),
      })),
      {
        กิจกรรมการเปิดบิล: "ตั๋วทั้งหมด",
        จำนวนตั๋ว: Number(sumActivity.count ?? 0),
        ยอดก่อนรวมภาษี: Number(sumActivity.amount ?? 0).toFixed(2),
        ยอดภาษี: Number(sumActivity.vat ?? 0).toFixed(2),
        ยอดสุทธิ: Number(sumActivity.total ?? 0).toFixed(2),
      },
    ];
    exportToCSV(dataset, `purchase_activities_${startDate}_to_${endDate}.csv`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Date Filter */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-8 bg-green-600 rounded-full"></span>
              สรุปยอดซื้อประจำวัน
            </h1>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่มต้น</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>
              <span className="text-gray-400">-</span>
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
             <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
              >
                <PrintIcon fontSize="small" />
                <span>พิมพ์รายงาน</span>
              </button>
              <button
                onClick={handleExportActivities}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
              >
                <ContentCopyIcon fontSize="small" />
                <span>EXPORT CSV</span>
              </button>
            </div>

            {/* Printable Content */}
            <div ref={printRef} className="print-content p-4">
              <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-gray-800">รายงานสรุปยอดซื้อประจำวัน</h2>
                <p className="text-gray-600 mt-1">
                  ระหว่างวันที่ {new Date(startDate).toLocaleDateString("th-TH")} - {new Date(endDate).toLocaleDateString("th-TH")}
                </p>
              </div>

              {/* NOTE: Inline styles for print layout safety if Tailwind not loaded in print view, 
                  but screen view uses Tailwind classes. */}
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                
                {/* Activity Table */}
                <div className="md:col-span-12">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                      <h3 className="text-lg font-bold text-gray-700">กิจกรรมการเปิดบิล</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-4">กิจกรรม</th>
                            <th className="px-6 py-4 text-right">จำนวนตั๋ว</th>
                            <th className="px-6 py-4 text-right">ยอดก่อนรวมภาษี</th>
                            <th className="px-6 py-4 text-right">ยอดภาษี</th>
                            <th className="px-6 py-4 text-right">ยอดสุทธิ</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {activityRows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-green-50/30 transition-colors">
                              <td className="px-6 py-4 font-medium text-gray-800">{row.name}</td>
                              <td className="px-6 py-4 text-right text-gray-600">{row.count}</td>
                              <td className="px-6 py-4 text-right text-gray-600">{formatNumber(row.amount)}</td>
                              <td className="px-6 py-4 text-right text-gray-600">{formatNumber(row.vat)}</td>
                              <td className="px-6 py-4 text-right font-medium text-gray-800">{formatNumber(row.total)}</td>
                            </tr>
                          ))}
                          <tr className="bg-green-50/50 font-bold text-gray-900 border-t border-green-100">
                            <td className="px-6 py-4">ตั๋วทั้งหมด</td>
                            <td className="px-6 py-4 text-right">{sumActivity.count}</td>
                            <td className="px-6 py-4 text-right">{formatNumber(sumActivity.amount)}</td>
                            <td className="px-6 py-4 text-right">{formatNumber(sumActivity.vat)}</td>
                            <td className="px-6 py-4 text-right text-green-700 text-base">{formatNumber(sumActivity.total)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Payment Table */}
                <div className="md:col-span-12 lg:col-span-6 lg:col-start-7">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-full">
                    <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                      <h3 className="text-lg font-bold text-gray-700">สรุปช่องทางชำระเงิน</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-4">ช่องทาง</th>
                            <th className="px-6 py-4 text-right">จำนวน</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {paymentRows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-orange-50/30 transition-colors">
                              <td className="px-6 py-4 font-medium text-gray-800">{row.method}</td>
                              <td className="px-6 py-4 text-right font-medium text-gray-800">{formatNumber(row.total)}</td>
                            </tr>
                          ))}
                          <tr className="bg-orange-50/50 font-bold text-gray-900 border-t border-orange-100">
                            <td className="px-6 py-4">ทั้งหมด</td>
                            <td className="px-6 py-4 text-right text-orange-700 text-base">{formatNumber(totalPayment)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
